// Side-effect import: `ImageSelect.module.scss` registers the global
// `.image-select` selector (consumed externally as a styling hook).
import 'css/src/components/common/ui/ImageSelect/ImageSelect.module.scss';

import React from 'react';
import type { GroupBase, Props, StylesConfig } from 'react-select';

import type { SelectCustomProps } from '../Select/Select';
import Select from '../Select/Select';

import CustomImageOption from 'js/src/components/common/ui/ImageSelect/CustomImageOption';
import CustomImageValue from 'js/src/components/common/ui/ImageSelect/CustomImageValue';
import { customReactSelectStyles } from 'js/src/components/common/ui/Select/custom-react-select-styles';
import CustomDropdownIndicator from 'js/src/components/common/ui/Select/CustomDropdownIndicator';

export type ImageSelectOptionType<T> = {
    className?: string;
    value: T;
    label: string;
    picture_url: string | null;
    disabled?: boolean;
    onFocus?: () => void;
};

const ImageSelect = <
    T = unknown,
    Option extends ImageSelectOptionType<T> = ImageSelectOptionType<T>,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
>(
    props: Omit<Props<Option, IsMulti, Group>, 'onChange'> & SelectCustomProps<T, IsMulti>,
) => {
    return (
        <Select
            {...props}
            className={`image-select ${props.className ?? ''}`}
            components={{
                DropdownIndicator: CustomDropdownIndicator,
                SingleValue: CustomImageValue<T, Option>,
                Option: CustomImageOption<T, Option>,
                ...props.components,
            }}
            styles={
                {
                    ...customReactSelectStyles,
                    ...{
                        input: (base, state) => {
                            return {
                                ...base,
                                // move the input out of the way of the image, if there is a value
                                // already set in the select
                                paddingLeft: props.value ? '28px' : undefined,
                            };
                        },
                    },
                } as StylesConfig<Option, IsMulti, Group>
            }
        />
    );
};

export default ImageSelect;
